import { FC } from 'react';
import {
  BorderProps,
  Flex,
  HStack,
  LayoutProps,
  Text,
  ThemingProps,
  TypographyProps,
} from '@chakra-ui/react';

import { formatAmount } from 'common/formatAmount';
import SunshineIcon from 'components/_core/SunshineIcon';

import { EvolutionArrowDirection } from '../DeltaValueHeader/types';
import { useArrowDirection } from './hooks/useArrowDirection';

const ORIENTATION_ANGLES: Record<EvolutionArrowDirection, number> = {
  down: 180,
  left: 270,
  right: 90,
  up: 0,
};

type EvolutionArrowSize = 'md' | 'sm';

export const EvolutionArrow: FC<{
  direction: EvolutionArrowDirection;
  color?: string | null;
  backgroundColor?: string | null;
  borderColor?: string | null;
  size?: EvolutionArrowSize;
}> = ({
  backgroundColor = 'white',
  borderColor = 'grey.800',
  color = 'grey.800',
  direction,
  size = 'md',
}) => {
  const defaultOrientationDegrees = 45;

  const CONTAINER_SIZE_MAPPING: Record<
    EvolutionArrowSize,
    LayoutProps['height']
  > = {
    md: 'space-32',
    sm: 'space-24',
  };

  const ICON_SIZE_MAPPING: Record<
    EvolutionArrowSize,
    ThemingProps<'SunshineIcon'>['size']
  > = {
    md: 'icon-20',
    sm: 'icon-16',
  };

  const BORDER_RADIUS_MAPPING: Record<
    EvolutionArrowSize,
    BorderProps['border']
  > = {
    md: 'radius-8',
    sm: 'radius-6',
  };

  const containerSize =
    CONTAINER_SIZE_MAPPING[size] || CONTAINER_SIZE_MAPPING.md;
  const iconSize = ICON_SIZE_MAPPING[size] || ICON_SIZE_MAPPING.md;
  const borderRadius = BORDER_RADIUS_MAPPING[size] || BORDER_RADIUS_MAPPING.md;

  const rotationDegrees =
    ORIENTATION_ANGLES[direction] - defaultOrientationDegrees;

  // This is a flexbox to prevent svg's line-height to interfere with padding/sizing
  return (
    <Flex
      alignItems="center"
      aspectRatio={1}
      backgroundColor={backgroundColor || 'white'}
      border="1px solid"
      borderColor={borderColor || 'grey.800'}
      borderRadius={borderRadius}
      height={containerSize}
      justifyContent="center"
      maxWidth={containerSize}
      padding="space-10"
      width={containerSize}
    >
      <SunshineIcon
        fill={color || 'grey.800'}
        name="transaction-out"
        size={iconSize}
        transform={`rotate(${rotationDegrees}deg)`}
      />
    </Flex>
  );
};

export const CurrentValueIndicator: FC<{
  currentValue?: number;
  delta: number | null;
  arrowColors: {
    arrow?: string | null;
    background?: string | null;
    border?: string | null;
  };
  titleFontSize?: TypographyProps['fontSize'];
  displayArrow?: boolean;
}> = ({
  arrowColors,
  currentValue,
  delta,
  displayArrow = true,
  titleFontSize = 'font-31',
}) => {
  const direction = useArrowDirection(delta);

  return (
    <HStack spacing="space-16">
      <Text fontSize={titleFontSize} fontWeight="weight-500" variant="primary">
        {formatAmount({
          forcePlusSign: false,
          isMinorUnitValue: true,
          value: currentValue ?? 0,
        })}
      </Text>
      {direction && displayArrow ? (
        <Flex alignItems="center" borderBottom="none" marginLeft="auto">
          <EvolutionArrow
            backgroundColor={arrowColors.background}
            borderColor={arrowColors.border}
            color={arrowColors.arrow}
            direction={direction}
          />
        </Flex>
      ) : null}
    </HStack>
  );
};
